<template>
  <div class="drafts-page">
    <CommonOverlay :show="loading" title="Loading Drafts" message="Please wait..." />

    <!-- Page Header -->
    <div class="drafts-header">
      <div class="header-left">
        <h1 class="page-title">Drafts</h1>
        <p class="page-subtitle">Manage your unfinished campaigns</p>
      </div>
      <div class="header-actions">
        <b-button variant="success" @click="$router.push('/app/campaigns')">
          <i class="fa fa-plus mr-2"></i> New Campaign
        </b-button>
      </div>
    </div>

    <!-- Sub-navigation for Campaigns section -->
    <div class="campaigns-subnav">
      <router-link to="/app/campaigns" class="subnav-link">
        <i class="fa fa-bullhorn"></i>
        <span>Campaigns</span>
      </router-link>
      <router-link
        to="/app/campaigns-drafts"
        class="subnav-link"
        :class="{ active: $route.path === '/app/campaigns-drafts' }"
      >
        <i class="fa fa-pencil-square-o"></i>
        <span>Drafts</span>
      </router-link>
      <router-link to="/app/campaigns-statistics" class="subnav-link">
        <i class="fa fa-bar-chart"></i>
        <span>Reports</span>
      </router-link>
      <router-link to="/app/campaigns-templates" class="subnav-link">
        <i class="fa fa-file-text-o"></i>
        <span>Templates</span>
      </router-link>
      <router-link to="/app/campaigns-ab-tests" class="subnav-link">
        <i class="fa fa-flask"></i>
        <span>A/B Tests</span>
      </router-link>
    </div>

    <!-- Search and Filter -->
    <div class="drafts-toolbar">
      <div class="search-box">
        <i class="fa fa-search"></i>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search drafts..."
          class="search-input"
        />
        <i
          v-if="searchQuery"
          class="fa fa-times-circle clear-btn"
          @click="searchQuery = ''"
        ></i>
      </div>

      <div class="filter-group">
        <b-form-select v-model="filterType" :options="typeOptions" class="filter-select"></b-form-select>
      </div>

      <div class="sort-group">
        <b-form-select v-model="sortBy" :options="sortOptions" class="filter-select"></b-form-select>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && filteredDrafts.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fa fa-pencil-square-o"></i>
      </div>
      <h4>No Drafts Found</h4>
      <p v-if="searchQuery">Try adjusting your search or filters</p>
      <p v-else>You don't have any draft campaigns. Create a new campaign to get started.</p>
      <b-button v-if="!searchQuery" variant="primary" @click="$router.push('/app/campaigns')">
        <i class="fa fa-plus mr-2"></i> Create Campaign
      </b-button>
    </div>

    <!-- Drafts List -->
    <div v-if="filteredDrafts.length > 0" class="drafts-list">
      <div
        v-for="draft in paginatedDrafts"
        :key="draft.CampaignID"
        class="draft-card"
      >
        <div class="draft-info">
          <div class="draft-icon" :class="draft.Type.toLowerCase()">
            <i :class="getTypeIcon(draft.Type)"></i>
          </div>
          <div class="draft-details">
            <h5 class="draft-name">{{ draft.Name }}</h5>
            <div class="draft-meta">
              <span class="meta-item">
                <i class="fa fa-calendar-o"></i>
                Last edited: {{ draft.UpdatedAt | dateformat('MMM D, YYYY h:mm A') }}
              </span>
              <span class="meta-item">
                <i class="fa fa-tag"></i>
                {{ draft.Type }}
              </span>
              <span v-if="draft.RecipientCount" class="meta-item">
                <i class="fa fa-users"></i>
                {{ draft.RecipientCount }} recipients
              </span>
            </div>
            <div class="draft-progress" v-if="draft.CompletionProgress !== undefined">
              <div class="progress-bar-container">
                <div class="progress-bar" :style="{ width: draft.CompletionProgress + '%' }"></div>
              </div>
              <span class="progress-text">{{ draft.CompletionProgress }}% complete</span>
            </div>
          </div>
        </div>

        <div class="draft-actions">
          <b-button variant="primary" size="sm" @click="editDraft(draft)">
            <i class="fa fa-pencil mr-1"></i> Continue Editing
          </b-button>
          <b-dropdown variant="link" no-caret right class="actions-dropdown">
            <template #button-content>
              <i class="fa fa-ellipsis-v"></i>
            </template>
            <b-dropdown-item @click="duplicateDraft(draft)">
              <i class="fa fa-copy mr-2"></i> Duplicate
            </b-dropdown-item>
            <b-dropdown-item @click="renameDraft(draft)">
              <i class="fa fa-pencil mr-2"></i> Rename
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item @click="deleteDraft(draft)" class="text-danger">
              <i class="fa fa-trash mr-2"></i> Delete
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination-wrapper">
      <b-pagination
        v-model="currentPage"
        :total-rows="filteredDrafts.length"
        :per-page="perPage"
        align="center"
        class="mb-0"
      ></b-pagination>
    </div>

    <!-- Delete Confirmation Modal -->
    <b-modal
      id="delete-draft-modal"
      ref="deleteModal"
      title="Delete Draft"
      centered
      @ok="confirmDelete"
      ok-variant="danger"
      ok-title="Delete"
    >
      <p>Are you sure you want to delete the draft <strong>"{{ draftToDelete?.Name }}"</strong>?</p>
      <p class="text-muted mb-0">This action cannot be undone.</p>
    </b-modal>

    <!-- Rename Modal -->
    <b-modal
      id="rename-draft-modal"
      ref="renameModal"
      title="Rename Draft"
      centered
      @ok="confirmRename"
      :ok-disabled="!newDraftName.trim()"
    >
      <b-form-group label="Draft Name">
        <b-form-input v-model="newDraftName" placeholder="Enter new name"></b-form-input>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */
import { mapState, mapActions, mapGetters } from 'vuex';
import CommonOverlay from '@/components/CommonOverlay';

export default {
  name: 'Drafts',

  components: {
    CommonOverlay
  },

  data() {
    return {
      loading: false,
      searchQuery: '',
      filterType: null,
      sortBy: 'updated',
      currentPage: 1,
      perPage: 10,
      draftToDelete: null,
      draftToRename: null,
      newDraftName: '',

      typeOptions: [
        { value: null, text: 'All Types' },
        { value: 'Email', text: 'Email' },
        { value: 'SMS', text: 'SMS' },
        { value: 'Both', text: 'Email & SMS' }
      ],

      sortOptions: [
        { value: 'updated', text: 'Last Updated' },
        { value: 'created', text: 'Date Created' },
        { value: 'name', text: 'Name (A-Z)' }
      ]
    };
  },

  async mounted() {
    await this.loadDrafts();
  },

  computed: {
    drafts() {
      // Filter store campaigns to drafts only
      const campaigns = this.$store.state.campaigns.campaigns || [];
      return campaigns.filter(c => c.Status === 'draft' || c.Status === 'Draft' || c.Status === 'list');
    },

    filteredDrafts() {
      let result = [...this.drafts];

      // Filter by search
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(d =>
          d.Name.toLowerCase().includes(query)
        );
      }

      // Filter by type
      if (this.filterType) {
        result = result.filter(d => d.Type === this.filterType);
      }

      // Sort
      result.sort((a, b) => {
        switch (this.sortBy) {
          case 'updated':
            return new Date(b.UpdatedAt) - new Date(a.UpdatedAt);
          case 'created':
            return new Date(b.CreatedAt) - new Date(a.CreatedAt);
          case 'name':
            return a.Name.localeCompare(b.Name);
          default:
            return 0;
        }
      });

      return result;
    },

    paginatedDrafts() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredDrafts.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredDrafts.length / this.perPage);
    }
  },

  methods: {
    async loadDrafts() {
      this.loading = true;
      try {
        await this.$store.dispatch('campaigns/fetchCampaigns');
      } catch (error) {
        console.error('Error loading drafts:', error);
      } finally {
        this.loading = false;
      }
    },

    getTypeIcon(type) {
      const icons = {
        'Email': 'fa fa-envelope',
        'SMS': 'fa fa-mobile',
        'Both': 'fa fa-clone'
      };
      return icons[type] || 'fa fa-file-text';
    },

    editDraft(draft) {
      // Navigate to campaigns page with edit query param
      this.$router.push({
        name: 'campaigns',
        query: { edit: draft.CampaignIDForWeb || draft.CampaignID }
      });
    },

    async duplicateDraft(draft) {
      try {
        const campaignId = draft.CampaignIDForWeb || draft.CampaignID;
        await this.$store.dispatch('campaigns/duplicateCampaign', campaignId);
        this.$toast?.success?.('Draft duplicated successfully');
        await this.loadDrafts();
      } catch (error) {
        console.error('Error duplicating draft:', error);
        this.$toast?.error?.('Failed to duplicate draft');
      }
    },

    renameDraft(draft) {
      this.draftToRename = draft;
      this.newDraftName = draft.Name;
      this.$refs.renameModal.show();
    },

    async confirmRename() {
      if (this.draftToRename && this.newDraftName.trim()) {
        try {
          const campaignId = this.draftToRename.CampaignIDForWeb || this.draftToRename.CampaignID;
          await this.$store.dispatch('campaigns/updateCampaign', {
            campaignId,
            campaignData: { name: this.newDraftName.trim() }
          });
          this.$toast?.success?.('Draft renamed successfully');
          await this.loadDrafts();
        } catch (error) {
          console.error('Error renaming draft:', error);
          this.$toast?.error?.('Failed to rename draft');
        }
      }
      this.draftToRename = null;
      this.newDraftName = '';
    },

    deleteDraft(draft) {
      this.draftToDelete = draft;
      this.$refs.deleteModal.show();
    },

    async confirmDelete() {
      if (this.draftToDelete) {
        try {
          const campaignId = this.draftToDelete.CampaignIDForWeb || this.draftToDelete.CampaignID;
          await this.$store.dispatch('campaigns/deleteCampaign', campaignId);
          this.$toast?.success?.('Draft deleted successfully');
        } catch (error) {
          console.error('Error deleting draft:', error);
          this.$toast?.error?.('Failed to delete draft');
        }
      }
      this.draftToDelete = null;
    }
  },

  filters: {
    dateformat(value, format) {
      if (!value) return '';
      const date = new Date(value);
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      };
      return date.toLocaleDateString('en-US', options);
    }
  }
};
</script>

<style lang="scss" scoped>
.drafts-page {
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
}

.drafts-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #212529;
    margin: 0;
  }

  .page-subtitle {
    color: #6c757d;
    margin: 4px 0 0;
    font-size: 14px;
  }
}

// Sub-navigation for Campaigns section
.campaigns-subnav {
  display: flex;
  gap: 4px;
  background: white;
  padding: 6px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow-x: auto;

  .subnav-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 8px;
    color: #64748b;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.9rem;
    white-space: nowrap;
    transition: all 0.2s ease;

    i {
      font-size: 14px;
    }

    &:hover {
      background: #f1f5f9;
      color: #334155;
    }

    &.active,
    &.router-link-exact-active {
      background: linear-gradient(135deg, #3abf94, #2d9474);
      color: white;
      box-shadow: 0 2px 4px rgba(58, 191, 148, 0.3);
    }
  }

  @media (max-width: 576px) {
    .subnav-link {
      padding: 8px 12px;
      font-size: 0.85rem;

      span {
        display: none;
      }

      i {
        font-size: 16px;
      }
    }
  }
}

.drafts-toolbar {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;

  .search-box {
    position: relative;
    flex: 1;
    min-width: 250px;
    max-width: 400px;

    .fa-search {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #adb5bd;
    }

    .search-input {
      width: 100%;
      padding: 10px 36px 10px 40px;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      font-size: 14px;

      &:focus {
        outline: none;
        border-color: #3abf94;
      }
    }

    .clear-btn {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #adb5bd;
      cursor: pointer;

      &:hover {
        color: #6c757d;
      }
    }
  }

  .filter-select {
    width: 150px;
    border-radius: 8px;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .empty-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 24px;
    background: #f0f4f8;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 36px;
      color: #6c757d;
    }
  }

  h4 {
    color: #212529;
    margin-bottom: 8px;
  }

  p {
    color: #6c757d;
    margin-bottom: 20px;
  }
}

.drafts-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.draft-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .draft-info {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex: 1;

    .draft-icon {
      width: 48px;
      height: 48px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      i {
        font-size: 20px;
        color: white;
      }

      &.email {
        background: linear-gradient(135deg, #667eea, #764ba2);
      }

      &.sms {
        background: linear-gradient(135deg, #11998e, #38ef7d);
      }

      &.both {
        background: linear-gradient(135deg, #f093fb, #f5576c);
      }
    }

    .draft-details {
      flex: 1;

      .draft-name {
        font-size: 16px;
        font-weight: 600;
        color: #212529;
        margin: 0 0 8px;
      }

      .draft-meta {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;

        .meta-item {
          font-size: 13px;
          color: #6c757d;

          i {
            margin-right: 4px;
          }
        }
      }

      .draft-progress {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 12px;

        .progress-bar-container {
          flex: 1;
          max-width: 200px;
          height: 6px;
          background: #e9ecef;
          border-radius: 3px;
          overflow: hidden;

          .progress-bar {
            height: 100%;
            background: linear-gradient(to right, #3abf94, #2d9474);
            border-radius: 3px;
            transition: width 0.3s ease;
          }
        }

        .progress-text {
          font-size: 12px;
          color: #6c757d;
          white-space: nowrap;
        }
      }
    }
  }

  .draft-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    .actions-dropdown {
      ::v-deep .btn-link {
        color: #6c757d;
        padding: 8px;

        &:hover {
          color: #212529;
        }
      }
    }
  }
}

.pagination-wrapper {
  margin-top: 24px;
  display: flex;
  justify-content: center;
}

// Responsive
@media (max-width: 768px) {
  .draft-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;

    .draft-actions {
      width: 100%;
      justify-content: flex-end;
    }
  }

  .draft-info .draft-meta {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
